import React from 'react';

import { Remove as RemoveIcon } from '@mui/icons-material';
import { Box, Divider, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { isNotNil } from 'es-toolkit';
import { DateTime, Interval } from 'luxon';

import { DateFormatPatterns } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';
import { numberWithCommas } from '@clearverify/utils';

import { DashboardUsageReportItemInfoFragment, DashboardVerificationRequestGroup } from 'src/api';
import { ChartBase, ChartOptions } from 'src/components/chart';
import { ButtonDatePicker } from 'src/components/common';
import { DashboardPalette } from 'src/constants/dashboard';
import { useDashboardPrintView } from 'src/hooks/dashboard';
import { snapToUtcMonthEnd, snapToUtcMonthStart } from 'src/utils/dashboard';

import { NOW } from '../constants';
import { DashboardChartWrapper } from '../DashboardChartWrapper';

export type DashboardUsageReportsBlockProps = {
  dashboardUsageReportsItems: DashboardUsageReportItemInfoFragment[];
  startDate: Nullable<DateTime>;
  endDate: Nullable<DateTime>;
  onStartDateChange?: (startDate: Nullable<DateTime>) => void;
  onEndDateChange?: (endDate: Nullable<DateTime>) => void;
  minDate?: Nullable<DateTime>;
  maxDate?: Nullable<DateTime>;
  loading?: boolean;
};

export const DashboardUsageReportsBlock: React.FC<DashboardUsageReportsBlockProps> = ({
  dashboardUsageReportsItems,
  onStartDateChange,
  onEndDateChange,
  startDate,
  endDate,
  minDate = NOW.minus({ months: 6 }),
  maxDate = NOW,
  loading,
}) => {
  const { isPrintView } = useDashboardPrintView();

  const handleStartDateChange = React.useCallback(
    (date: Nullable<DateTime>) => onStartDateChange?.(snapToUtcMonthStart(date)),
    [onStartDateChange],
  );

  const handleEndDateChange = React.useCallback(
    (date: Nullable<DateTime>) => onEndDateChange?.(snapToUtcMonthEnd(date)),
    [onEndDateChange],
  );

  const interval = startDate && endDate ? Interval.fromDateTimes(startDate, endDate) : undefined;

  const months = interval
    ?.splitBy({ months: 1 })
    .map(interval => interval.start?.toFormat(DateFormatPatterns.fullMonthYear))
    .filter(isNotNil);

  const isDatasetEmpty = !dashboardUsageReportsItems.length && !loading;

  const options = React.useMemo(
    (): ChartOptions => ({
      grid: { top: 10, right: 40, bottom: 55, left: 40 },
      animationDuration: isPrintView ? 0 : undefined,
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          fontSize: 14,
          fontWeight: 'bold',
        },
      },
      yAxis: {
        type: 'value',
        position: 'right',
        axisLabel: {
          fontSize: 14,
          fontWeight: 'bold',
        },
      },
      legend: {
        show: true,
        bottom: 0,
        textStyle: {
          fontSize: 13,
        },
      },
      tooltip: {
        trigger: 'axis',
        valueFormatter: value => numberWithCommas(Number(value?.valueOf())),
      },
      series: [
        {
          name: VERIFICATION_REQUEST_TYPE_TO_TITLE_MAP[
            DashboardVerificationRequestGroup.VerificationOfEmployment
          ],
          datasetId: DashboardVerificationRequestGroup.VerificationOfEmployment,
          type: 'bar',
          encode: {
            x: 'month',
            y: 'count',
          },
          label: isPrintView ? LABEL_OPTIONS : {},
          itemStyle: {
            borderRadius: 5,
          },
          color: loading ? DashboardPalette.slate50 : DashboardPalette.grey,
        },
        {
          name: VERIFICATION_REQUEST_TYPE_TO_TITLE_MAP[
            DashboardVerificationRequestGroup.VerificationOfIncome
          ],
          datasetId: DashboardVerificationRequestGroup.VerificationOfIncome,
          type: 'bar',
          encode: {
            x: 'month',
            y: 'count',
          },
          label: isPrintView ? LABEL_OPTIONS : {},
          itemStyle: {
            borderRadius: 5,
          },
          color: loading ? DashboardPalette.slate50 : DashboardPalette.green400,
        },
        {
          name: VERIFICATION_REQUEST_TYPE_TO_TITLE_MAP[
            DashboardVerificationRequestGroup.Governmental
          ],
          datasetId: DashboardVerificationRequestGroup.Governmental,
          type: 'bar',
          encode: {
            x: 'month',
            y: 'count',
          },
          label: isPrintView ? LABEL_OPTIONS : {},
          itemStyle: {
            borderRadius: 5,
          },
          color: loading ? DashboardPalette.slate50 : DashboardPalette.green200,
        },
        {
          name: VERIFICATION_REQUEST_TYPE_TO_TITLE_MAP[
            DashboardVerificationRequestGroup.Reverification
          ],
          datasetId: DashboardVerificationRequestGroup.Reverification,
          type: 'bar',
          encode: {
            x: 'month',
            y: 'count',
          },
          label: isPrintView ? LABEL_OPTIONS : {},
          itemStyle: {
            borderRadius: 5,
          },
          color: loading ? DashboardPalette.slate50 : DashboardPalette.blue200,
        },
      ],
      dataset: [
        {
          id: 'dataset_raw',
          dimensions: ['month', 'count', 'type'],
          source: dashboardUsageReportsItems,
        },
        {
          id: DashboardVerificationRequestGroup.VerificationOfEmployment,
          fromDatasetId: 'dataset_raw',
          transform: [
            {
              type: 'filter',
              config: {
                dimension: 'type',
                '=': DashboardVerificationRequestGroup.VerificationOfEmployment,
              },
            },
          ],
        },
        {
          id: DashboardVerificationRequestGroup.VerificationOfIncome,
          fromDatasetId: 'dataset_raw',
          transform: [
            {
              type: 'filter',
              config: {
                dimension: 'type',
                '=': DashboardVerificationRequestGroup.VerificationOfIncome,
              },
            },
          ],
        },
        {
          id: DashboardVerificationRequestGroup.Governmental,
          fromDatasetId: 'dataset_raw',
          transform: [
            {
              type: 'filter',
              config: {
                dimension: 'type',
                '=': DashboardVerificationRequestGroup.Governmental,
              },
            },
          ],
        },
        {
          id: DashboardVerificationRequestGroup.Reverification,
          fromDatasetId: 'dataset_raw',
          transform: [
            {
              type: 'filter',
              config: {
                dimension: 'type',
                '=': DashboardVerificationRequestGroup.Reverification,
              },
            },
          ],
        },
      ],
    }),
    [dashboardUsageReportsItems, isPrintView, loading, months],
  );

  return (
    <Paper sx={{ py: 2.5, px: 3, borderRadius: 2, overflow: 'hidden' }} variant="outlined">
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1">Verification Report Usage</Typography>
          <Typography variant="body1" color="text.secondary">
            Verification report usage allows you to track different types of verification across an
            adjustable period of time for your employee base.
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please note: It is important to understand that reverifications will show as a duplicate
            count. They are represented in the count of Verification of Income, Verification of
            Employment and Governmental reports.
          </Typography>
        </Box>
        <Box height="4px">{loading ? <LinearProgress color="secondary" /> : <Divider />}</Box>

        <Stack direction="row" alignItems="center" spacing={1}>
          <ButtonDatePicker
            views={['month', 'year']}
            timezone="UTC"
            minDate={minDate || undefined}
            maxDate={endDate || maxDate || undefined}
            value={startDate}
            onChange={handleStartDateChange}
            disabled={loading}
          />
          <Box display="flex" alignItems="center" fontSize="13px">
            <RemoveIcon fontSize="inherit" />
          </Box>
          <ButtonDatePicker
            views={['month', 'year']}
            timezone="UTC"
            minDate={minDate || undefined}
            maxDate={maxDate || undefined}
            value={endDate}
            onChange={handleEndDateChange}
            disabled={loading}
          />
        </Stack>

        {Boolean(months?.length) && (
          <DashboardChartWrapper
            isDatasetEmpty={isDatasetEmpty}
            isPrintView={isPrintView}
            wrapperHeight={555}
          >
            <ChartBase option={options} style={{ height: 555 }} />
          </DashboardChartWrapper>
        )}
      </Stack>
    </Paper>
  );
};

const LABEL_OPTIONS = {
  show: true,
  fontSize: '16px',
  fontWeight: 'bold' as const,
  formatter: '{@[1]}',
  position: 'inside' as const,
  offset: [60, 0],
  rotate: 90,
  inside: false,
};

const VERIFICATION_REQUEST_TYPE_TO_TITLE_MAP = {
  [DashboardVerificationRequestGroup.VerificationOfEmployment]: 'Verification Of Employment',
  [DashboardVerificationRequestGroup.VerificationOfIncome]: 'Verification Of Income',
  [DashboardVerificationRequestGroup.Governmental]: 'Governmental',
  [DashboardVerificationRequestGroup.Reverification]: 'Reverification',
} as const;
